import { create } from 'zustand';
import { Vector3 } from 'three';
import {
  ShotData,
  ShotComparisonData,
  VisualizationConfig,
  ShotAnimation,
  CameraSettings,
  ShotVisualizationStore
} from '../types';
import { TrajectoryCalculator, CRICKET_PHYSICS } from '../utils/physics';
import { ShotOptimizer } from '../utils/optimization';

// Default configuration
const defaultConfig: VisualizationConfig = {
  showActualTrajectory: true,
  showOptimalTrajectory: true,
  showField: true,
  showBall: true,
  showBat: true,
  showMetrics: true,
  cameraView: 'side',
  animationSpeed: 1.0,
  ballTrailLength: 20
};

const defaultAnimation: ShotAnimation = {
  phases: {
    preImpact: { name: 'Pre-Impact', duration: 2000, startTime: 0, endTime: 2000 },
    impact: { name: 'Impact', duration: 100, startTime: 2000, endTime: 2100 },
    postImpact: { name: 'Post-Impact', duration: 4000, startTime: 2100, endTime: 6100 }
  },
  totalDuration: 6100,
  currentPhase: 'preImpact',
  currentTime: 0,
  isPlaying: false,
  speed: 1.0
};

const defaultCamera: CameraSettings = {
  position: new Vector3(10, 8, 15),
  target: new Vector3(0, 2, 0),
  fov: 50,
  near: 0.1,
  far: 200
};

export const useShotVisualizationStore = create<ShotVisualizationStore>((set, get) => {
  const trajectoryCalc = new TrajectoryCalculator();
  const shotOptimizer = new ShotOptimizer();
  
  return {
    // Data state
    currentShot: null,
    shotComparison: null,
    
    // Visualization state
    config: defaultConfig,
    animation: defaultAnimation,
    camera: defaultCamera,
    
    // Physics
    physicsParams: CRICKET_PHYSICS,
    
    // Actions
    loadShot: async (shot: ShotData) => {
      set({ currentShot: shot });
      
      // Calculate optimization and trajectories
      const optimization = await shotOptimizer.optimizeShot(shot);
      
      // Calculate actual trajectory
      const actualTrajectory = trajectoryCalc.calculateTrajectory(
        shot.exitVelocity,
        shot.launchAngle,
        shot.horizontalAngle,
        shot.spinRate
      );
      
      // Calculate optimal trajectory
      const optimalTrajectory = trajectoryCalc.calculateTrajectory(
        optimization.optimal.exitVelocity,
        optimization.optimal.launchAngle,
        optimization.optimal.horizontalAngle,
        optimization.optimal.spinRate
      );
      
      // Create comparison data
      const optimalShotData: ShotData = {
        ...shot,
        exitVelocity: optimization.optimal.exitVelocity,
        launchAngle: optimization.optimal.launchAngle,
        horizontalAngle: optimization.optimal.horizontalAngle,
        spinRate: optimization.optimal.spinRate,
        impactLocationX: optimization.optimal.impactLocation.x,
        impactLocationY: optimization.optimal.impactLocation.y,
        distance: optimalTrajectory.totalDistance,
        height: optimalTrajectory.maxHeight,
        quality: 'excellent'
      };
      
      const shotComparison: ShotComparisonData = {
        actual: shot,
        optimal: optimalShotData,
        optimization,
        actualTrajectory,
        optimalTrajectory
      };
      
      set({ shotComparison });
    },
    
    calculateOptimal: async (shot: ShotData) => {
      return shotOptimizer.optimizeShot(shot);
    },
    
    updateConfig: (newConfig: Partial<VisualizationConfig>) => {
      set(state => ({
        config: { ...state.config, ...newConfig }
      }));
    },
    
    playAnimation: () => {
      set(state => ({
        animation: {
          ...state.animation,
          isPlaying: true,
          currentTime: 0,
          currentPhase: 'preImpact'
        }
      }));
      
      // Start animation loop
      const animate = () => {
        const state = get();
        if (!state.animation.isPlaying) return;
        
        const newTime = state.animation.currentTime + (16 * state.animation.speed); // 60fps
        
        if (newTime >= state.animation.totalDuration) {
          set(state => ({
            animation: {
              ...state.animation,
              isPlaying: false,
              currentTime: state.animation.totalDuration,
              currentPhase: 'postImpact'
            }
          }));
          return;
        }
        
        // Determine current phase
        let currentPhase = 'preImpact';
        if (newTime >= state.animation.phases.impact.startTime) {
          currentPhase = 'impact';
        }
        if (newTime >= state.animation.phases.postImpact.startTime) {
          currentPhase = 'postImpact';
        }
        
        set(state => ({
          animation: {
            ...state.animation,
            currentTime: newTime,
            currentPhase
          }
        }));
        
        requestAnimationFrame(animate);
      };
      
      requestAnimationFrame(animate);
    },
    
    pauseAnimation: () => {
      set(state => ({
        animation: {
          ...state.animation,
          isPlaying: false
        }
      }));
    },
    
    resetAnimation: () => {
      set(state => ({
        animation: {
          ...state.animation,
          currentTime: 0,
          currentPhase: 'preImpact',
          isPlaying: false
        }
      }));
    },
    
    setCameraView: (view: string) => {
      let newCamera: CameraSettings;
      
      switch (view) {
        case 'side':
          newCamera = {
            position: new Vector3(10, 8, 15),
            target: new Vector3(0, 2, 0),
            fov: 50,
            near: 0.1,
            far: 200
          };
          break;
        case 'behind':
          newCamera = {
            position: new Vector3(0, 3, -15),
            target: new Vector3(0, 2, 0),
            fov: 60,
            near: 0.1,
            far: 200
          };
          break;
        case 'above':
          newCamera = {
            position: new Vector3(0, 50, -8), // Higher up, centered on batsman
            target: new Vector3(0, 0, -8), // Look at batsman position
            fov: 60,
            near: 0.1,
            far: 200
          };
          break;
        case 'free':
          // Keep current camera position for free view
          newCamera = get().camera;
          break;
        default:
          newCamera = defaultCamera;
      }
      
      set(state => ({
        config: { ...state.config, cameraView: view as any },
        camera: newCamera
      }));
    }
  };
});